import React, { useState } from 'react';
import { Plane, Check, Send } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../data/mockData';
import { aircraftData } from '../data/aircraftData';
import { Card } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const Aircraft = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const [activeTab, setActiveTab] = useState('overview');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    aircraft_model: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const categories = [
    { key: 'transportLight', label: { fr: 'Transport Léger', en: 'Light Transport' }, data: aircraftData.transportLight },
    { key: 'transportMedium', label: { fr: 'Transport Moyen', en: 'Medium Transport' }, data: aircraftData.transportMedium },
    { key: 'training', label: { fr: 'Entraînement', en: 'Training' }, data: aircraftData.training },
    { key: 'coin', label: { fr: 'Appui Armé (COIN)', en: 'Armed Support (COIN)' }, data: aircraftData.coin },
    { key: 'helicoptersLight', label: { fr: 'Hélicoptères Légers', en: 'Light Helicopters' }, data: aircraftData.helicoptersLight },
    { key: 'helicoptersMedium', label: { fr: 'Hélicoptères Moyens', en: 'Medium Helicopters' }, data: aircraftData.helicoptersMedium },
    { key: 'maritime', label: { fr: 'Patrouille Maritime', en: 'Maritime Patrol' }, data: aircraftData.maritime },
    { key: 'drones', label: { fr: 'Drones MALE', en: 'MALE Drones' }, data: aircraftData.drones },
    { key: 'vip', label: { fr: 'Avions VIP', en: 'VIP Aircraft' }, data: aircraftData.vip },
    { key: 'medevac', label: { fr: 'EVASAN (Médicalisés)', en: 'MEDEVAC' }, data: aircraftData.medevac }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section
        className="relative py-32 overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1562805612-9c340007e0b9?auto=format&fit=crop&q=80)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/95 via-blue-900/90 to-slate-800/95"></div>
        <div className="relative z-10 container mx-auto px-4 text-center">
          <div className="inline-block p-3 bg-blue-600/20 backdrop-blur-sm rounded-full mb-6">
            <Plane className="h-12 w-12 text-blue-300" />
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            {t.aircraft.title}
          </h1>
          <p className="text-xl text-gray-200 max-w-2xl mx-auto">
            {t.aircraft.subtitle}
          </p>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-lg text-gray-700 leading-relaxed">
              {t.aircraft.intro}
            </p>
          </div>
        </div>
      </section>

      {/* Main Content with Tabs */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="container mx-auto px-4">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full max-w-2xl mx-auto grid-cols-2 mb-12 bg-white shadow-lg">
              <TabsTrigger value="overview" className="text-base py-3">
                {language === 'fr' ? 'Vue d\'ensemble' : 'Overview'}
              </TabsTrigger>
              <TabsTrigger value="fleet" className="text-base py-3">
                {language === 'fr' ? 'Catalogue Flotte' : 'Fleet Catalog'}
              </TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview" className="space-y-12">
              {/* Categories Overview */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {categories.map(({ key, label, data }) => (
                  <Card
                    key={key}
                    className="overflow-hidden hover:shadow-xl transition-all duration-300 border-2 hover:border-blue-500 group bg-white cursor-pointer"
                    onClick={() => setActiveTab('fleet')}
                  >
                    {/* Aircraft Category Image */}
                    <div className="relative h-48 overflow-hidden">
                      <img 
                        src={data[0]?.image || 'https://images.unsplash.com/photo-1562805612-9c340007e0b9?auto=format&fit=crop&q=80'}
                        alt={label[language]}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 to-transparent"></div>
                      <div className="absolute bottom-4 left-4">
                        <div className="w-12 h-12 bg-white/90 backdrop-blur-sm rounded-lg flex items-center justify-center">
                          <Plane className="h-6 w-6 text-blue-600" />
                        </div>
                      </div>
                    </div>
                    
                    {/* Aircraft Category Content */}
                    <div className="p-6 space-y-3">
                      <h3 className="text-xl font-bold text-gray-900">
                        {label[language]}
                      </h3>
                      <p className="text-gray-600 text-sm">
                        {data.length} {language === 'fr' ? 'modèles disponibles' : 'models available'}
                      </p>
                    </div>
                  </Card>
                ))}
              </div>

              {/* Integrated Services */}
              <div className="mt-16">
                <Card className="p-8 bg-white border-2 border-blue-200">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">
                    {t.aircraft.services.title}
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {t.aircraft.services.items.map((service, index) => (
                      <div key={index} className="flex items-center space-x-3 p-4 rounded-lg bg-gradient-to-r from-blue-50 to-slate-50">
                        <Check className="h-5 w-5 text-blue-600 flex-shrink-0" />
                        <span className="text-gray-700">{service}</span>
                      </div>
                    ))}
                  </div>
                </Card>
              </div>
            </TabsContent>

            {/* Fleet Catalog Tab */}
            <TabsContent value="fleet" className="space-y-8">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  {language === 'fr' ? 'Catalogue Complet des Aéronefs' : 'Complete Aircraft Catalog'}
                </h2>
                <p className="text-gray-600">
                  {language === 'fr'
                    ? 'Explorez notre gamme complète d\'aéronefs par catégorie'
                    : 'Explore our complete range of aircraft by category'}
                </p>
              </div>

              <Accordion type="single" collapsible className="space-y-4">
                {categories.map(({ key, label, data }) => (
                  <AccordionItem
                    key={key}
                    value={key}
                    className="border-2 border-gray-200 rounded-xl bg-white overflow-hidden hover:border-blue-500 transition-colors"
                  >
                    <AccordionTrigger className="px-6 py-4 hover:no-underline hover:bg-blue-50 transition-colors">
                      <div className="flex items-center space-x-4">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                          <Plane className="h-5 w-5 text-blue-600" />
                        </div>
                        <div className="text-left">
                          <h3 className="text-xl font-bold text-gray-900">
                            {label[language]}
                          </h3>
                          <p className="text-sm text-gray-600">
                            {data.length} {language === 'fr' ? 'modèles disponibles' : 'models available'}
                          </p>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-6 pb-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pt-4">
                        {data.map((aircraft, index) => (
                          <Card key={index} className="overflow-hidden bg-white border-2 border-gray-200 hover:border-blue-500 transition-all hover:shadow-lg">
                            {/* Aircraft Image */}
                            {aircraft.image && (
                              <div className="relative h-48 overflow-hidden">
                                <img 
                                  src={aircraft.image}
                                  alt={aircraft.name}
                                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent"></div>
                                <div className="absolute bottom-3 left-3 right-3">
                                  <h4 className="font-bold text-white text-lg leading-tight">{aircraft.name}</h4>
                                </div>
                              </div>
                            )}
                            
                            {/* Aircraft Details */}
                            <div className="p-4 space-y-3">
                              {/* Basic Info */}
                              <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-gray-600">{language === 'fr' ? 'Pays' : 'Country'}:</span>
                                  <span className="font-medium text-gray-900">{aircraft.country}</span>
                                </div>
                                {aircraft.range && (
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">{language === 'fr' ? 'Portée' : 'Range'}:</span>
                                    <span className="font-medium text-gray-900">{aircraft.range}</span>
                                  </div>
                                )}
                                {aircraft.capacity && (
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">{language === 'fr' ? 'Capacité' : 'Capacity'}:</span>
                                    <span className="font-medium text-gray-900">{aircraft.capacity}</span>
                                  </div>
                                )}
                                {aircraft.speed && (
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">{language === 'fr' ? 'Vitesse' : 'Speed'}:</span>
                                    <span className="font-medium text-gray-900">{aircraft.speed}</span>
                                  </div>
                                )}
                                {aircraft.endurance && (
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">{language === 'fr' ? 'Endurance' : 'Endurance'}:</span>
                                    <span className="font-medium text-gray-900">{aircraft.endurance}</span>
                                  </div>
                                )}
                                {aircraft.mtow && (
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">MTOW:</span>
                                    <span className="font-medium text-gray-900">{aircraft.mtow}</span>
                                  </div>
                                )}
                                {aircraft.ceiling && (
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">{language === 'fr' ? 'Plafond' : 'Ceiling'}:</span>
                                    <span className="font-medium text-gray-900">{aircraft.ceiling}</span>
                                  </div>
                                )}
                                {aircraft.crew && (
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">{language === 'fr' ? 'Équipage' : 'Crew'}:</span>
                                    <span className="font-medium text-gray-900">{aircraft.crew}</span>
                                  </div>
                                )}
                              </div>

                              {/* Engine */}
                              {aircraft.engine && (
                                <div className="pt-3 border-t border-gray-200">
                                  <span className="text-xs text-gray-600 uppercase block mb-1">{language === 'fr' ? 'Motorisation' : 'Engine'}:</span>
                                  <p className="text-sm text-gray-900">{aircraft.engine}</p>
                                </div>
                              )}

                              {/* Role */}
                              {aircraft.role && (
                                <div className="pt-3 border-t border-gray-200">
                                  <span className="text-xs text-gray-600 uppercase block mb-1">{language === 'fr' ? 'Rôle' : 'Role'}:</span>
                                  <p className="text-sm text-gray-900">{aircraft.role}</p>
                                </div>
                              )}

                              {/* Features */}
                              {aircraft.features && aircraft.features.length > 0 && (
                                <div className="pt-3 border-t border-gray-200">
                                  <span className="text-xs text-gray-600 uppercase block mb-2">{language === 'fr' ? 'Atouts' : 'Features'}:</span>
                                  <div className="flex flex-wrap gap-1">
                                    {aircraft.features.slice(0, 3).map((feature, idx) => (
                                      <span key={idx} className="inline-block px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-full">
                                        {feature}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </Card>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Custom Aircraft Request Form */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                {language === 'fr' 
                  ? 'Modèle non listé ?' 
                  : 'Model not listed?'}
              </h2>
              <p className="text-lg text-gray-600">
                {language === 'fr'
                  ? 'Demandez un aéronef spécifique non présent dans notre catalogue'
                  : 'Request a specific aircraft not in our catalog'}
              </p>
            </div>

            <Card className="p-8 border-2 border-blue-200 bg-gradient-to-br from-white to-blue-50">
              <form onSubmit={async (e) => {
                e.preventDefault();
                setIsSubmitting(true);

                try {
                  const backendUrl = process.env.REACT_APP_BACKEND_URL;
                  const response = await fetch(`${backendUrl}/api/aircraft-request`, {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(formData),
                  });

                  if (response.ok) {
                    toast.success(
                      language === 'fr' 
                        ? 'Demande envoyée avec succès ! Nous vous contacterons bientôt.' 
                        : 'Request sent successfully! We will contact you soon.'
                    );
                    setFormData({
                      name: '',
                      email: '',
                      phone: '',
                      aircraft_model: '',
                      message: ''
                    });
                  } else {
                    throw new Error('Failed to submit');
                  }
                } catch (error) {
                  toast.error(
                    language === 'fr' 
                      ? 'Erreur lors de l\'envoi. Veuillez réessayer.' 
                      : 'Error sending request. Please try again.'
                  );
                } finally {
                  setIsSubmitting(false);
                }
              }} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {language === 'fr' ? 'Nom complet *' : 'Full name *'}
                    </label>
                    <Input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder={language === 'fr' ? 'Votre nom' : 'Your name'}
                      className="w-full"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {language === 'fr' ? 'Email *' : 'Email *'}
                    </label>
                    <Input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="email@example.com"
                      className="w-full"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {language === 'fr' ? 'Téléphone' : 'Phone'}
                    </label>
                    <Input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+221 XX XXX XX XX"
                      className="w-full"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {language === 'fr' ? 'Modèle d\'aéronef *' : 'Aircraft model *'}
                    </label>
                    <Input
                      type="text"
                      required
                      value={formData.aircraft_model}
                      onChange={(e) => setFormData({ ...formData, aircraft_model: e.target.value })}
                      placeholder={language === 'fr' ? 'Ex: Boeing 737 MAX' : 'Ex: Boeing 737 MAX'}
                      className="w-full"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {language === 'fr' ? 'Message / Spécifications' : 'Message / Specifications'}
                  </label>
                  <Textarea
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder={language === 'fr' 
                      ? 'Décrivez vos besoins spécifiques, quantité, délais, etc.' 
                      : 'Describe your specific needs, quantity, timeline, etc.'}
                    className="w-full min-h-[120px]"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 text-lg"
                >
                  {isSubmitting ? (
                    language === 'fr' ? 'Envoi en cours...' : 'Sending...'
                  ) : (
                    <>
                      <Send className="mr-2 h-5 w-5" />
                      {language === 'fr' ? 'Envoyer la demande' : 'Send request'}
                    </>
                  )}
                </Button>

                <p className="text-sm text-gray-600 text-center">
                  {language === 'fr'
                    ? '* Champs obligatoires. Nous vous répondrons dans les 24-48 heures.'
                    : '* Required fields. We will respond within 24-48 hours.'}
                </p>
              </form>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            {language === 'fr'
              ? 'Intéressé par nos capacités aériennes ?'
              : 'Interested in our air capabilities?'}
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            {language === 'fr'
              ? 'Contactez-nous pour une consultation personnalisée'
              : 'Contact us for a personalized consultation'}
          </p>
          <a href="/contact">
            <button className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors text-lg">
              {language === 'fr' ? 'Demander une consultation' : 'Request a consultation'}
            </button>
          </a>
        </div>
      </section>
    </div>
  );
};

export default Aircraft;